/**
 * Mobile background upload using @capgo/capacitor-uploader
 * Handles iOS and Android native uploads with progress tracking
 */

import { Uploader } from '@capgo/capacitor-uploader'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'

export interface UploadParams {
  has_music?: boolean
  content_type?: 'video' | 'photo'
  mission?: string
  package?: string
  add_on?: string
  questionnaire_upload?: boolean
  edited?: boolean
  endpoint?: string // Custom endpoint (default: 'content/upload/')
}

/**
 * Initialization pattern to avoid race conditions
 */
let uploaderInitialized = false
const ensureUploaderInitialized = async (): Promise<void> => {
  if (uploaderInitialized) {
    return Promise.resolve()
  }
  console.log('[Uploader] First use detected - minimal initialization delay...')
  await new Promise((resolve) => setTimeout(resolve, 100))
  uploaderInitialized = true
  console.log('[Uploader] ✅ Initialization delay complete')
}

/**
 * Get JWT token for authentication
 */
const getJWT = async (): Promise<string> => {
  const result = await FirebaseAuthentication.getIdToken()
  if (!result || !result.token) {
    throw new Error('Failed to get authentication token')
  }
  return result.token
}

/**
 * Upload file using Capacitor Uploader (iOS/Android native)
 */
export const uploadBackgroundFile = async (
  filePath: string, // Native file path (e.g., "file:///path/to/file.mp4")
  mimeType: string, // MIME type (e.g., "video/mp4")
  onProgress: (progress: number) => void,
  params?: UploadParams
): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    let listenerHandle: any = null
    let uploadId: string | null = null

    try {
      // Step 1: Ensure plugin is initialized (adds small delay on first use)
      await ensureUploaderInitialized()

      // Step 2: Build the upload URL with query parameters
      // Backend endpoint: POST content/upload/ (for mobile direct upload)
      // If not provided, use the default endpoint
      const baseEndpoint = params?.endpoint || 'content/upload/'
      let serverUrl = import.meta.env.VITE_API_ENDPOINT + baseEndpoint
      const queryParams: string[] = []

      // Add optional query parameters
      if (params?.questionnaire_upload) {
        queryParams.push('questionnaire_upload=true')
      }
      if (params?.package) {
        queryParams.push(`package=${params.package}`)
      }
      if (params?.content_type) {
        queryParams.push(`content_type=${params.content_type}`)
      }
      if (params?.has_music !== undefined) {
        queryParams.push(`has_music=${params.has_music}`)
      }
      if (params?.add_on) {
        queryParams.push(`add_on=${params.add_on}`)
      }
      if (params?.mission) {
        queryParams.push(`mission=${encodeURIComponent(params.mission)}`)
      }
      if (params?.edited !== undefined) {
        queryParams.push(`edited=${params.edited}`)
      }

      if (queryParams.length > 0) {
        serverUrl += '?' + queryParams.join('&')
      }

      // Step 3: Set up event listener BEFORE starting upload
      // This is critical - listener must be active before upload starts
      listenerHandle = await Uploader.addListener('events', (data) => {
        // CRITICAL: Use IIFE to prevent returning values (WebKit can't serialize functions)
        ; (async () => {
          try {
            // Only process events for THIS upload
            if (uploadId && data.id === uploadId) {
              console.log(`Upload event for ${uploadId}:`, data.name, data.payload)

              if (data.name === 'uploading') {
                // Progress update
                const percent = data.payload?.percent ?? 0
                onProgress(percent)
              } else if (data.name === 'completed') {
                // Upload successful
                const fileId = (data.payload as any)?.id ?? ''

                // Clean up listener
                if (listenerHandle) {
                  await listenerHandle.remove().catch((e: any) =>
                    console.warn('Failed to remove listener:', e)
                  )
                }

                resolve(fileId)
              } else if (data.name === 'failed') {
                // Upload failed
                const errorMessage = data.payload?.error ?? 'Upload failed'
                const statusCode = data.payload?.statusCode
                const errorCode = (data.payload as any)?.errorCode ?? 'UNKNOWN'

                // Clean up listener
                if (listenerHandle) {
                  await listenerHandle.remove().catch((e: any) =>
                    console.warn('Failed to remove listener:', e)
                  )
                }

                // Create detailed error
                let detailedError = errorMessage
                if (statusCode) {
                  detailedError += ` (HTTP ${statusCode})`
                }
                if (errorCode) {
                  detailedError += ` (Code: ${errorCode})`
                }

                reject(new Error(detailedError))
              }
            }
          } catch (error) {
            // If listener crashes, reject to prevent hanging
            if (listenerHandle) {
              await listenerHandle.remove().catch((e: any) =>
                console.warn('Failed to remove listener:', e)
              )
            }
            reject(
              new Error(
                `Event listener error: ${error instanceof Error ? error.message : String(error)}`
              )
            )
          }
        })()
        // No return statement - callback must not return anything
      })

      // Step 4: Get authentication token
      const jwtToken = await getJWT()

      // Step 5: Clean file path (remove file:// prefix if needed)
      let cleanPath = filePath.replace('file://', '')
      try {
        cleanPath = decodeURIComponent(cleanPath)
      } catch (e) {
        console.warn('Failed to decode file path, using as-is:', cleanPath)
      }

      // Step 6: Start the upload
      const result = await Uploader.startUpload({
        serverUrl: serverUrl,
        filePath: cleanPath, // Native file path (no file:// prefix)
        method: 'POST', // HTTP method
        mimeType: mimeType, // MIME type
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Connection: 'keep-alive',
        },
      })

      uploadId = result.id
      onProgress(1) // Initial progress

      // Step 7: Set timeout to prevent hanging forever
      const timeoutMs = 10 * 60 * 1000 // 10 minutes
      const timeoutHandle = setTimeout(() => {
        console.error(`Upload ${uploadId} timed out after ${timeoutMs}ms`)
        if (listenerHandle) {
          listenerHandle.remove().catch((e: any) =>
            console.warn('Failed to remove listener:', e)
          )
        }
        reject(new Error('Upload timed out'))
      }, timeoutMs)

      // Override resolve/reject to clear timeout
      const originalResolve = resolve
      const originalReject = reject

      resolve = (value: any) => {
        clearTimeout(timeoutHandle)
        originalResolve(value)
      }

      reject = (reason: any) => {
        clearTimeout(timeoutHandle)
        originalReject(reason)
      }
    } catch (error) {
      console.error('Error starting upload:', error)
      // Clean up listener if it was added
      if (listenerHandle) {
        await listenerHandle.remove().catch((e: any) =>
          console.warn('Failed to remove listener:', e)
        )
      }
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  })
}
